import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Paper,
  Grid,
  TextField,
} from "@mui/material";
import { Add, Settings } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { get, post } from "requests";
import MDBox from "../../../../components/MDBox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

interface RequestStipulationComponentProps {
  applicationId: number;
  showRequestStipulations: boolean;
  setShowStips: (show: boolean) => void;
  setShowManageStips: (show: boolean) => void;
  refreshStipulationsforApp: () => void;
}

interface StipulationType {
  id: number;
  type: string;
  default: boolean;
  active: boolean;
}

const RequestStipulationComponent: React.FC<RequestStipulationComponentProps> = ({
  applicationId,
  showRequestStipulations,
  setShowStips,
  setShowManageStips,
  refreshStipulationsforApp,
}) => {
  const [selectedStips, setSelectedStips] = useState<StipulationType[]>([]);
  const [selectedWithNotes, setSelectedWithNotes] = useState<any[]>([]);
  const [allStips, setAllStips] = useState<StipulationType[]>([]);

  useEffect(() => {
    fetchAssignableStipulations();
  }, []);

  const fetchAssignableStipulations = () => {
    get(`stipulations/getAssignableStipulationsForApp/${applicationId}`).then((response) => {
      setAllStips(response);
    });
  };

  const handleSelectedStipsChange = (event: any) => {
    const selectedIds = event.target.value;
    const selectedStips = allStips.filter((stip) => selectedIds.includes(stip.id));

    setSelectedStips(selectedStips);
    setSelectedWithNotes(
      selectedStips.map((stip) => ({
        id: stip.id,
        notes: selectedWithNotes.find((item) => item.id === stip.id)?.notes || "",
      }))
    );
  };

  const handleNoteChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id: number
  ) => {
    const updatedSelectedWithNotes = selectedWithNotes.map((stip) =>
      stip.id === id ? { ...stip, notes: event.target.value } : stip
    );
    setSelectedWithNotes(updatedSelectedWithNotes);
  };

  const resetModal = () => {
    setShowStips(false);
  };

  const saveStipulationForDealApplication = () => {
    if (selectedWithNotes.length === 0) {
      alert("Please select at least one item.");
      return;
    }
    const stipRequest = {
      applicationId: applicationId,
      stipulationDetails: selectedWithNotes,
    };
    post("application/saveApplicationStipulation", stipRequest).then(() => {
      resetModal();
      refreshStipulationsforApp();
    });
  };

  return (
    <Dialog open={showRequestStipulations} onClose={resetModal} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Add sx={{ mr: 1 }} />
            <Typography variant="h5">ADD STIPULATIONS</Typography>
          </MDBox>
          <IconButton
            onClick={() => {
              resetModal();
              setShowManageStips(true);
            }}
            style={{ cursor: "pointer" }}
          >
            <Settings sx={{ color: "grey" }} />
          </IconButton>
        </MDBox>
      </DialogTitle>

      <DialogContent>
        <FormControl fullWidth>
          <InputLabel
            id="multi-select-label"
            shrink={selectedStips.length > 0}
            sx={{
              fontSize: "15px",
              transform: "translate(0, 1.5px) scale(0.75)",
              "&.Mui-focused": {
                transform: "translate(0, 1.5px) scale(0.75)",
              },
            }}
          >
            Select Stipulations
          </InputLabel>
          <Select
            id="multi-select"
            multiple
            value={selectedStips.map((stip) => stip.id)}
            onChange={handleSelectedStipsChange}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return null;
              }
              return selected
                .map((id) => {
                  const selectedStip = allStips.find((stip) => stip.id === id);
                  return selectedStip ? selectedStip.type : "";
                })
                .join(", ");
            }}
            displayEmpty
            sx={{
              fontSize: "15px",
              width: "95%",
              height: 40,
              fontStyle: "italic",
              borderBottom: "1px solid #ccc",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 48 * 8,
                  maxWidth: 350,
                },
              },
            }}
            IconComponent={() => null}
            endAdornment={
              <KeyboardArrowDownIcon
                sx={{
                  position: "absolute",
                  right: "10px",
                  pointerEvents: "none",
                  color: "#000",
                }}
              />
            }
          >
            {allStips.length === 0 ? (
              <MenuItem disabled>No Items available</MenuItem>
            ) : (
              allStips.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={selectedStips.some((stip) => stip.id === option.id)} />
                  <ListItemText
                    primary={<Typography style={{ fontSize: "14px" }}>{option.type}</Typography>}
                  />
                </MenuItem>
              ))
            )}
          </Select>
          <Paper
            style={{ marginTop: "20px", width: "100%", padding: "20px", border: "1px solid #ccc" }}
          >
            {selectedWithNotes.length === 0 ? (
              <Grid item>
                <Typography variant="body1" style={{ fontSize: "15px" }}>
                  No items selected.
                </Typography>
              </Grid>
            ) : (
              <Grid container direction="column">
                <Grid
                  container
                  item
                  direction="row"
                  justifyContent="space-between"
                  style={{ marginBottom: "10px" }}
                >
                  <Grid item style={{ width: "50%" }}>
                    <Typography variant="body1" style={{ fontSize: "15px", fontWeight: "bold" }}>
                      Type
                    </Typography>
                  </Grid>
                  <Grid item style={{ width: "50%" }}>
                    <Typography variant="body1" style={{ fontSize: "15px", fontWeight: "bold" }}>
                      Notes
                    </Typography>
                  </Grid>
                </Grid>

                {selectedStips.map((stip) => (
                  <Grid
                    key={stip.id}
                    container
                    item
                    direction="row"
                    justifyContent="space-between"
                    style={{ marginBottom: "10px" }}
                  >
                    <Grid item style={{ width: "50%" }}>
                      <Typography style={{ fontSize: "15px" }}>{stip.type}</Typography>
                    </Grid>
                    <Grid item style={{ width: "50%" }}>
                      <TextField
                        placeholder="notes"
                        variant="outlined"
                        value={
                          (selectedWithNotes.find((item) => item.id === stip.id) || {}).notes || ""
                        }
                        onChange={(event) => handleNoteChange(event, stip.id)}
                        InputProps={{ style: { fontSize: 15 } }}
                        InputLabelProps={{ style: { fontSize: 14 } }}
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <MDButton
          color="error"
          variant="contained"
          onClick={resetModal}
          style={{ marginRight: "10px" }}
        >
          Cancel
        </MDButton>
        <MDButton color="warning" variant="contained" onClick={saveStipulationForDealApplication}>
          SUBMIT
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default RequestStipulationComponent;
